import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import HealthIndex from '../components/HealthIndex';
import PatientProviderAssignment from '../components/PatientProviderAssignment';
import VitalsChart from '../components/VitalsChart';
import HealthIndexChart from '../components/HealthIndexChart';
import AlertSettings from '../components/AlertSettings';
import SleepThresholds from '../components/SleepThresholds';
import SleepVitalsChart from '../components/SleepVitalsChart';
import SleepSessions from '../components/SleepSessions';
import { User, Patient, Vital, Facility, Device, DeviceType, DEVICE_CAPABILITIES } from '../types';
import { getPatient, getPatientVitals, getPatientLatestVitals, generateEnrollmentLink, getEnrollmentStatus, transferPatient, getFacilities, deleteEnrollmentInvitation, getPatientSleepSummaries, getSleepSessions, getPatientDevices } from '../services/api';
import axios from 'axios';
import { DemoDataBadge, SYNTHETIC_SOURCE, PatientDataProvenance } from '../components/DemoDataBadge';
import CollapsibleSection from '../components/CollapsibleSection';

interface PatientDetailProps {
  user: User;
  onLogout: () => void;
  facilities?: Facility[];
  selectedFacility?: string;
  onFacilityChange?: (facilityId: string) => void;
}

interface AlertConfig {
  id: string;
  patient_id: string;
  vital_type: string;
  min_threshold: number | null;
  max_threshold: number | null;
  enabled: boolean;
}

const PatientDetail: React.FC<PatientDetailProps> = ({ user, onLogout, facilities, selectedFacility, onFacilityChange }) => {
  const { id } = useParams<{ id: string }>();
  const [patient, setPatient] = useState<Patient | null>(null);
  const [vitals, setVitals] = useState<Vital[]>([]);
  const [latestVitals, setLatestVitals] = useState<Vital[]>([]);
  const [sleepSummaries, setSleepSummaries] = useState<any[]>([]);
  const [sleepSessions, setSleepSessions] = useState<any[]>([]);
  const [alertConfigs, setAlertConfigs] = useState<AlertConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingThreshold, setEditingThreshold] = useState<string | null>(null);
  const [tempThresholds, setTempThresholds] = useState<{[key: string]: {min: string, max: string}}>({});
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollmentData, setEnrollmentData] = useState<any>(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState<any>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [allFacilities, setAllFacilities] = useState<Facility[]>([]);
  const [selectedTransferFacility, setSelectedTransferFacility] = useState('');
  const [transferring, setTransferring] = useState(false);
  const [patientDevices, setPatientDevices] = useState<Device[]>([]);

  useEffect(() => {
    if (id) {
      loadPatientData();
      loadEnrollmentStatus();
    }
  }, [id]);

  useEffect(() => {
    if (user.role === 'admin') {
      loadFacilities();
    }
  }, [user.role]);

  const loadFacilities = async () => {
    try {
      const facilitiesData = await getFacilities();
      setAllFacilities(facilitiesData);
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadEnrollmentStatus = async () => {
    try {
      const status = await getEnrollmentStatus(id!);
      setEnrollmentStatus(status);
    } catch (error) {
      console.error('Error loading enrollment status:', error);
    }
  };

  const loadPatientData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [patientData, vitalsData, latestData, alertConfigData, sleepData, sleepSessionsData, devicesData] = await Promise.all([
        getPatient(id!),
        getPatientVitals(id!, 720),
        getPatientLatestVitals(id!),
        axios.get(`${process.env.REACT_APP_API_URL}/api/alerts/config/patient/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(res => res.data).catch(() => []),
        getPatientSleepSummaries(id!, 14).catch(() => []),
        getSleepSessions(id!, 14).catch(() => []),
        getPatientDevices(id!).catch(() => [])
      ]);
      setPatient(patientData);
      setVitals(vitalsData);
      setLatestVitals(latestData);
      setAlertConfigs(alertConfigData);
      setSleepSummaries(sleepData);
      setSleepSessions(sleepSessionsData);
      setPatientDevices(devicesData);
    } catch (error) {
      console.error('Error loading patient data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditThreshold = (vitalType: string, config: AlertConfig | undefined) => {
    setEditingThreshold(vitalType);
    setTempThresholds({
      ...tempThresholds,
      [vitalType]: {
        min: config?.min_threshold?.toString() || '',
        max: config?.max_threshold?.toString() || ''
      }
    });
  };

  const handleSaveThreshold = async (vitalType: string, configId?: string) => {
    try {
      const token = localStorage.getItem('token');
      const thresholds = tempThresholds[vitalType];

      if (configId) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/alerts/config/${configId}`,
          {
            min_threshold: thresholds.min ? parseFloat(thresholds.min) : null,
            max_threshold: thresholds.max ? parseFloat(thresholds.max) : null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/alerts/config`,
          {
            patient_id: id,
            vital_type: vitalType,
            min_threshold: thresholds.min ? parseFloat(thresholds.min) : null,
            max_threshold: thresholds.max ? parseFloat(thresholds.max) : null,
            enabled: true
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setEditingThreshold(null);
      await loadPatientData();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to save threshold');
    }
  };

  const handleCancelEdit = () => {
    setEditingThreshold(null);
    setTempThresholds({});
  };

  const handleEnrollDevice = async (deviceType: string, email?: string, sendEmail?: boolean) => {
    try {
      setEnrolling(true);
      const result = await generateEnrollmentLink({
        patientId: id!,
        deviceType,
        sendEmail: sendEmail || false,
        email: email || undefined,
        sendSMS: false
      });
      setEnrollmentData(result);
      await loadEnrollmentStatus();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to generate enrollment link');
    } finally {
      setEnrolling(false);
    }
  };

  const handleDeleteEnrollment = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to delete this enrollment? This will allow you to restart the enrollment process.')) {
      return;
    }

    try {
      await deleteEnrollmentInvitation(invitationId);
      await loadEnrollmentStatus();
      alert('Enrollment deleted successfully. You can now enroll a new device.');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete enrollment');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleTransferPatient = async () => {
    if (!selectedTransferFacility) {
      alert('Please select a facility');
      return;
    }

    if (selectedTransferFacility === patient?.facility_id) {
      alert('Patient is already in this facility');
      return;
    }

    try {
      setTransferring(true);
      const updatedPatient = await transferPatient(id!, selectedTransferFacility);
      setPatient(updatedPatient);
      setShowTransferModal(false);
      setSelectedTransferFacility('');
      alert('Patient transferred successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to transfer patient');
    } finally {
      setTransferring(false);
    }
  };

  // Helper function to check if patient has a device that supports a specific metric
  const patientHasCapability = (metric: string): boolean => {
    if (patientDevices.length === 0) return true;
    return patientDevices.some(device => {
      const deviceType = device.device_type as DeviceType;
      const capabilities = DEVICE_CAPABILITIES[deviceType];
      return capabilities?.includes(metric);
    });
  };

  // Helper function to check if there's actual vitals data for a specific type
  const hasVitalsData = (vitalType: string): boolean => {
    return vitals.some(v => v.vital_type === vitalType);
  };

  // Helper function to transform sleep_sessions to emfit summary format for SleepVitalsChart
  const transformSleepSessionsToSummaries = (sessions: any[]): any[] => {
    return sessions.map(session => ({
      sleep_start: session.start_time,
      duration_in_sleep: session.duration_minutes * 60,
      duration_in_deep: session.deep_sleep_minutes * 60,
      duration_in_rem: session.rem_sleep_minutes * 60,
      sleep_score: session.sleep_score,
      bedexit_count: session.interruptions || 0,
      avg_hr: session.heart_rate_avg,
      avg_rr: session.respiratory_rate_avg,
      hrv_rmssd_morning: null,
      hrv_rmssd_evening: null,
    }));
  };

  // Get chart data: prefer emfit summaries, fallback to transformed sleep sessions
  const getSleepChartData = (): any[] => {
    if (sleepSummaries.length > 0) {
      return sleepSummaries;
    }
    if (sleepSessions.length > 0) {
      return transformSleepSessionsToSummaries(sleepSessions);
    }
    return [];
  };

  if (loading || !patient) {
    return (
      <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
        <div className="text-center">Loading...</div>
      </Layout>
    );
  }

  const vitalTypes = ['health_index', 'heart_rate', 'spo2', 'respiratory_rate', 'blood_pressure', 'temperature'];

  return (
    <Layout user={user} onLogout={onLogout} facilities={facilities} selectedFacility={selectedFacility} onFacilityChange={onFacilityChange}>
      <div className="px-4 sm:px-0">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {patient.first_name} {patient.last_name}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Room {patient.room_number} • {patient.gender} • Born {new Date(patient.date_of_birth).toLocaleDateString()}
          </p>
        </div>

        {/* Transfer Patient Section - Admin Only */}
        {user.role === 'admin' && (
          <div className="bg-gradient-to-r from-green-50 to-teal-50 shadow rounded-lg p-6 mb-6 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">🏥</span>
                  Current Facility
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {patient.facility_name || 'No facility assigned'}
                </p>
              </div>
              <button
                onClick={() => setShowTransferModal(true)}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                Transfer Patient
              </button>
            </div>
          </div>
        )}

        {/* Device Enrollment Section */}
        {(user.role === 'admin' || user.role === 'nurse' || user.role === 'doctor') && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 shadow rounded-lg p-6 mb-6 border border-blue-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">📱</span>
                  Device Enrollment
                </h2>
                <p className="text-sm text-gray-600 mt-1">Enroll patient in research study with wearable devices</p>
              </div>
              <button
                onClick={() => setShowEnrollModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
              >
                + Enroll Device
              </button>
            </div>

            {/* Currently Assigned Devices */}
            {patientDevices.length > 0 && (
              <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Assigned Devices:</h3>
                <div className="space-y-2">
                  {patientDevices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {device.device_type === 'apple_watch' ? '⌚' :
                           device.device_type === 'oura' ? '💍' :
                           device.device_type === 'emfit' ? '🛏️' :
                           device.device_type === 'biostrap' ? '📱' :
                           device.device_type === 'withings_bp' ? '🩺' :
                           device.device_type === 'withings_scale' ? '⚖️' : '📟'}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900 capitalize">
                            {device.device_type.replace(/_/g, ' ')}
                          </div>
                          <div className="text-sm text-green-700 font-mono">
                            S/N: {device.serial_number || device.device_id || 'N/A'}
                          </div>
                          {device.last_sync && (
                            <div className="text-xs text-gray-500">
                              Last sync: {new Date(device.last_sync).toLocaleString()}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          device.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : device.status === 'inactive'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {device.status === 'active' ? '✓ Active' :
                           device.status === 'inactive' ? 'Inactive' : 'Maintenance'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Enrollment Status */}
            {enrollmentStatus && enrollmentStatus.enrollments && enrollmentStatus.enrollments.length > 0 ? (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Active Enrollments:</h3>
                <div className="space-y-2">
                  {enrollmentStatus.enrollments.map((enrollment: any) => (
                    <div key={enrollment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">
                          {enrollment.device_type === 'apple_watch' ? '⌚' :
                           enrollment.device_type === 'oura' ? '💍' : '📱'}
                        </span>
                        <div>
                          <div className="font-medium text-gray-900 capitalize">
                            {enrollment.device_type.replace('_', ' ')}
                          </div>
                          <div className="text-xs text-gray-500">
                            Created {new Date(enrollment.created_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {enrollment.completed_at ? (
                          <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                            ✓ Connected
                          </span>
                        ) : new Date(enrollment.expires_at) < new Date() ? (
                          <>
                            <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                              Expired
                            </span>
                            <button
                              onClick={() => handleDeleteEnrollment(enrollment.id)}
                              className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                              title="Delete this enrollment to restart"
                            >
                              Delete
                            </button>
                          </>
                        ) : (
                          <>
                            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                              ⏳ Pending
                            </span>
                            <button
                              onClick={() => handleDeleteEnrollment(enrollment.id)}
                              className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm font-medium"
                              title="Delete this enrollment to restart"
                            >
                              Reset
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : patientDevices.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No devices assigned yet. Click "Enroll Device" to get started.
              </div>
            ) : null}
          </div>
        )}

        {/* Enrollment Modal */}
        {showEnrollModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Enroll New Device</h2>
                  <button
                    onClick={() => {
                      setShowEnrollModal(false);
                      setEnrollmentData(null);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>

                {!enrollmentData ? (
                  <div>
                    <p className="text-gray-600 mb-6">
                      Select a device type to generate an enrollment invitation for {patient.first_name} {patient.last_name}.
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                      <button
                        onClick={() => handleEnrollDevice('apple_watch')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">⌚</div>
                        <div className="font-medium text-gray-900 text-sm">Apple Watch</div>
                        <div className="text-xs text-gray-500 mt-1">HR, SpO2, Temp</div>
                      </button>

                      <button
                        onClick={() => handleEnrollDevice('oura')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">💍</div>
                        <div className="font-medium text-gray-900 text-sm">Oura Ring</div>
                        <div className="text-xs text-gray-500 mt-1">HR, HRV, Sleep</div>
                      </button>

                      <button
                        onClick={() => handleEnrollDevice('emfit')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">🛏️</div>
                        <div className="font-medium text-gray-900 text-sm">Emfit</div>
                        <div className="text-xs text-gray-500 mt-1">Sleep, HR, RR</div>
                      </button>

                      <button
                        onClick={() => handleEnrollDevice('biostrap')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">📱</div>
                        <div className="font-medium text-gray-900 text-sm">Biostrap</div>
                        <div className="text-xs text-gray-500 mt-1">HR, HRV, SpO2</div>
                      </button>

                      <button
                        onClick={() => handleEnrollDevice('withings_bp')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">🩺</div>
                        <div className="font-medium text-gray-900 text-sm">Withings BP</div>
                        <div className="text-xs text-gray-500 mt-1">Blood Pressure</div>
                      </button>

                      <button
                        onClick={() => handleEnrollDevice('withings_scale')}
                        disabled={enrolling}
                        className="p-4 border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                      >
                        <div className="text-3xl mb-2">⚖️</div>
                        <div className="font-medium text-gray-900 text-sm">Withings Scale</div>
                        <div className="text-xs text-gray-500 mt-1">Weight, Body Comp</div>
                      </button>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h3 className="font-medium text-gray-900 mb-2">📧 Send Invitation Email (Demo)</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        In production, this would send an email with setup instructions to the patient.
                      </p>
                      <div className="flex space-x-2">
                        <input
                          type="email"
                          placeholder="patient@example.com"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                          id="enrollment-email"
                        />
                        <button
                          onClick={() => {
                            const email = (document.getElementById('enrollment-email') as HTMLInputElement).value;
                            if (email) handleEnrollDevice('apple_watch', email, true);
                          }}
                          disabled={enrolling}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          Send Email
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center">
                        <span className="text-2xl mr-3">✅</span>
                        <div>
                          <div className="font-medium text-green-900">Enrollment Created!</div>
                          <div className="text-sm text-green-700">Share the link below with the patient</div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Setup Link:</label>
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          value={enrollmentData.setupURL}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm"
                        />
                        <button
                          onClick={() => copyToClipboard(enrollmentData.setupURL)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">QR Code:</label>
                      <div className="text-center bg-white p-4 border border-gray-300 rounded-lg">
                        <img
                          src={enrollmentData.qrCodeURL}
                          alt="Setup QR Code"
                          className="mx-auto"
                          style={{ maxWidth: '200px' }}
                        />
                        <p className="text-sm text-gray-500 mt-2">Patient can scan with their iPhone camera</p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <span className="text-xl mr-2">⏰</span>
                        <div className="text-sm">
                          <div className="font-medium text-gray-900">Link expires in {enrollmentData.expiresIn}</div>
                          <div className="text-gray-600 mt-1">Make sure the patient completes setup before expiration</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transfer Patient Modal */}
        {showTransferModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">Transfer Patient</h2>
                  <button
                    onClick={() => {
                      setShowTransferModal(false);
                      setSelectedTransferFacility('');
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-gray-600 mb-4">
                    Transfer {patient.first_name} {patient.last_name} to a different facility.
                  </p>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                    <div className="text-sm">
                      <span className="font-medium">Current Facility:</span>
                      <span className="ml-2 text-gray-700">{patient.facility_name || 'None'}</span>
                    </div>
                  </div>

                  <label htmlFor="transfer-facility" className="block text-sm font-medium text-gray-700 mb-2">
                    Select New Facility:
                  </label>
                  <select
                    id="transfer-facility"
                    value={selectedTransferFacility}
                    onChange={(e) => setSelectedTransferFacility(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">-- Select Facility --</option>
                    {allFacilities
                      .filter(f => f.id !== patient.facility_id)
                      .map(facility => (
                        <option key={facility.id} value={facility.id}>
                          {facility.name}
                        </option>
                      ))
                    }
                  </select>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowTransferModal(false);
                      setSelectedTransferFacility('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleTransferPatient}
                    disabled={transferring || !selectedTransferFacility}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {transferring ? 'Transferring...' : 'Transfer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Health Index (Visensia) */}
        <div className="mb-6">
          <HealthIndex
            patientId={id!}
            showCalculateButton={true}
            userRole={user.role}
          />
        </div>

        {/* Data provenance — what is real here and what is generated */}
        <PatientDataProvenance
          isDemo={!!(patient as any)?.is_demo}
          syntheticModalities={Array.from(
            new Set(latestVitals.filter(v => v.source === SYNTHETIC_SOURCE).map(v => v.vital_type))
          )}
        />

        {/* Care Team */}
        {(user.role === 'admin' || user.role === 'nurse') && (
          <div className="mb-6">
            <PatientProviderAssignment patientId={id!} />
          </div>
        )}

        {/* Latest Vitals */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Latest Vitals</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {latestVitals
              .filter(vital => patientHasCapability(vital.vital_type))
              .map((vital) => (
              <div key={vital.id} className="border rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">
                  {vital.vital_type.replace('_', ' ').toUpperCase()}
                  {vital.source === SYNTHETIC_SOURCE && <DemoDataBadge />}
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {vital.vital_type === 'blood_pressure'
                    ? `${Math.round(vital.systolic || 0)}/${Math.round(vital.diastolic || 0)}`
                    : ['heart_rate', 'respiratory_rate'].includes(vital.vital_type)
                    ? Math.round(vital.value)
                    : typeof vital.value === 'number' ? vital.value.toFixed(1) : '0.0'}
                </div>
                <div className="text-xs text-gray-500">{vital.unit}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {new Date(vital.recorded_at).toLocaleTimeString()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sleep Vitals - Show if emfit summaries OR sleep sessions available */}
        {getSleepChartData().length > 0 && (
          <div className="bg-white shadow rounded-lg p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Latest Sleep Data</h2>
            {(() => {
              const chartData = getSleepChartData();
              const latest = chartData[0];
              const formatDuration = (seconds: number) => {
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                return `${hours}h ${minutes}m`;
              };

              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Total Sleep</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatDuration(latest.duration_in_sleep)}
                      </div>
                      <div className="text-xs text-gray-400 mt-1">
                        {new Date(latest.sleep_start).toLocaleDateString()}
                      </div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Deep Sleep</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatDuration(latest.duration_in_deep)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {((latest.duration_in_deep / latest.duration_in_sleep) * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">REM Sleep</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {formatDuration(latest.duration_in_rem)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {((latest.duration_in_rem / latest.duration_in_sleep) * 100).toFixed(0)}%
                      </div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Sleep Score</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {latest.sleep_score}
                      </div>
                      <div className="text-xs text-gray-500">/100</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Bed Exits</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {latest.bedexit_count}
                      </div>
                      <div className="text-xs text-gray-500">exits</div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Avg Heart Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {latest.avg_hr ? parseFloat(latest.avg_hr).toFixed(0) : '--'}
                      </div>
                      <div className="text-xs text-gray-500">bpm</div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">Avg Resp. Rate</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {latest.avg_rr ? parseFloat(latest.avg_rr).toFixed(1) : '--'}
                      </div>
                      <div className="text-xs text-gray-500">br/min</div>
                    </div>

                    <div className="border rounded-lg p-4">
                      <div className="text-sm text-gray-600 mb-1">HRV (Morning)</div>
                      <div className="text-2xl font-bold text-gray-900">
                        {latest.hrv_rmssd_morning ? parseFloat(latest.hrv_rmssd_morning).toFixed(0) : '--'}
                      </div>
                      <div className="text-xs text-gray-500">ms</div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* Sleep Trends Chart */}
        {getSleepChartData().length > 0 && (
          <SleepVitalsChart data={getSleepChartData()} />
        )}

        {/* Sleep Sessions */}
        <SleepSessions sessions={sleepSessions} />

        {/* Alert Thresholds Configuration */}
        <CollapsibleSection title="Alert Thresholds" icon="🎚️">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {vitalTypes.filter(type => type === 'health_index' || patientHasCapability(type)).map((type) => {
              const config = alertConfigs.find(c => c.vital_type === type);
              const isEditing = editingThreshold === type;
              const tempValues = tempThresholds[type];

              return (
                <div key={type} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-700 text-sm">
                      {type === 'health_index' ? 'HEALTH INDEX (VSI)' : type.replace('_', ' ').toUpperCase()}
                    </h3>
                    {!isEditing && user.role !== 'caregiver' && (
                      <button
                        onClick={() => handleEditThreshold(type, config)}
                        className="text-primary-600 hover:text-primary-800 text-xs"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {isEditing ? (
                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Min</label>
                        <input
                          type="number"
                          value={tempValues?.min || ''}
                          onChange={(e) => setTempThresholds({
                            ...tempThresholds,
                            [type]: { ...tempValues, min: e.target.value }
                          })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Min"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Max</label>
                        <input
                          type="number"
                          value={tempValues?.max || ''}
                          onChange={(e) => setTempThresholds({
                            ...tempThresholds,
                            [type]: { ...tempValues, max: e.target.value }
                          })}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                          placeholder="Max"
                        />
                      </div>
                      <div className="flex space-x-2 mt-2">
                        <button
                          onClick={() => handleSaveThreshold(type, config?.id)}
                          className="flex-1 px-2 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 px-2 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-600">
                      <div>Min: {config?.min_threshold !== null ? config?.min_threshold : '-'}</div>
                      <div>Max: {config?.max_threshold !== null ? config?.max_threshold : '-'}</div>
                      <div className="text-xs mt-1">
                        {config?.enabled ? (
                          <span className="text-green-600">✓ Enabled</span>
                        ) : (
                          <span className="text-gray-400">Disabled</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CollapsibleSection>

        {/* Alert Settings - Clustering and Grouping Configuration */}
        {(user.role === 'admin' || user.role === 'nurse' || user.role === 'doctor') && (
          <div className="mb-6">
            <AlertSettings patientId={id!} />
            <SleepThresholds patientId={id!} />
          </div>
        )}

        {/* Health Index Trend Chart */}
        <div className="mb-6">
          <HealthIndexChart patientId={id!} />
        </div>

        {/* Vital Charts with Modern Visualization - only show if there's data */}
        <div className="space-y-6">
          {hasVitalsData('heart_rate') && patientHasCapability('heart_rate') && (
            <VitalsChart
              data={vitals}
              vitalType="heart_rate"
              vitalName="Heart Rate"
              unit="bpm"
              color="#ef4444"
              normalRange={{ min: 60, max: 100 }}
              criticalRange={{ min: 40, max: 120 }}
            />
          )}

          {hasVitalsData('spo2') && patientHasCapability('spo2') && (
            <VitalsChart
              data={vitals}
              vitalType="spo2"
              vitalName="Oxygen Saturation (SpO2)"
              unit="%"
              color="#3b82f6"
              normalRange={{ min: 95, max: 100 }}
              criticalRange={{ min: 90, max: 100 }}
            />
          )}

          {hasVitalsData('blood_pressure') && patientHasCapability('blood_pressure') && (
            <VitalsChart
              data={vitals}
              vitalType="blood_pressure"
              vitalName="Blood Pressure"
              unit="mmHg"
              color="#8b5cf6"
              normalRange={{ min: 90, max: 140 }}
              criticalRange={{ min: 160, max: 200 }}
            />
          )}

          {hasVitalsData('temperature') && patientHasCapability('temperature') && (
            <VitalsChart
              data={vitals}
              vitalType="temperature"
              vitalName="Body Temperature"
              unit="°F"
              color="#f59e0b"
              normalRange={{ min: 97.0, max: 99.0 }}
              criticalRange={{ min: 95.0, max: 103.0 }}
            />
          )}

          {hasVitalsData('respiratory_rate') && patientHasCapability('respiratory_rate') && (
            <VitalsChart
              data={vitals}
              vitalType="respiratory_rate"
              vitalName="Respiratory Rate"
              unit="breaths/min"
              color="#10b981"
              normalRange={{ min: 12, max: 20 }}
              criticalRange={{ min: 8, max: 30 }}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default PatientDetail;
